<template>
  <div>
    <b-row>
      <b-col cols="12">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h4 class="mb-0">
              Head-to-Head Comparison
            </h4>
            <p
              v-if="isLoggedIn && usersFullName"
              class="text-muted mt-1 mb-0"
            >
              Generated for {{ usersFullName }}<template v-if="companyName">
                of {{ companyName }}
              </template> on {{ generatedDate }}
            </p>
          </div>
          <b-button
            variant="secondary"
            @click="$router.back()"
          >
            Back
          </b-button>
        </div>

        <b-card
          v-if="!loading && !error"
          no-body
        >
          <div class="table-responsive">
            <table class="table table-bordered mb-0 comparison-table">
              <thead>
                <tr>
                  <th class="field-column" />
                  <th
                    v-for="reportId in reportIds"
                    :key="'header-' + reportId"
                    class="report-column text-center"
                  >
                    <router-link
                      :to="{ name: 'product-report', params: { reportNumber: reportId } }"
                      class="report-link"
                    >
                      {{ reportId }}
                    </router-link>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(heading, rowIndex) in visibleHeadings"
                  :key="'row-' + rowIndex"
                >
                  <td class="field-name">
                    {{ heading }}
                  </td>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <td
                    v-for="(reportId, colIndex) in reportIds"
                    :key="'cell-' + rowIndex + '-' + colIndex"
                    class="text-left"
                    v-html="getCellValue(colIndex, rowIndex)"
                  />
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>

        <div v-if="loading">
          <loading-container />
        </div>

        <no-data-container
          v-else-if="error"
          :message="errorMessage || 'Failed to load comparison data'"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import LoadingContainer from '@/views/components/LoadingContainer.vue'
import NoDataContainer from '@/views/components/NoDataContainer.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    LoadingContainer,
    NoDataContainer,
  },
  data() {
    return {
      generatedDate: new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
      reportIds: [],
      headingNames: [],
      comparisonData: [],
      loading: false,
      error: false,
      errorMessage: '',
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isLoggedIn',
      'usersFullName',
    ]),
    companyName() {
      return this.$store.state.auth.user.companyname
    },
    visibleHeadings() {
      // Exclude the last 6 headings (calculated prob returns)
      return this.headingNames.slice(0, -6)
    },
  },
  watch: {
    '$route.params.reportNumber': {
      handler(newReportIds) {
        // Watch for changes to route params and reload data - need this because mounted is only called on first open
        if (newReportIds && newReportIds.length > 0) {
          this.reportIds = newReportIds
          // Save to sessionStorage for page reloads
          sessionStorage.setItem('headToHeadReportIds', JSON.stringify(newReportIds))
          this.loadReports()
        }
      },
      deep: true,
    },
  },
  mounted() {
    // Get report IDs from route params
    this.reportIds = this.$route.params.reportNumber || []

    // If no route params, try to restore from sessionStorage (page refreshed)
    if (this.reportIds.length === 0) {
      const stored = sessionStorage.getItem('headToHeadReportIds')
      if (stored) {
        try {
          this.reportIds = JSON.parse(stored)
        } catch (e) {
          console.error('Failed to parse stored report IDs:', e)
        }
      }
    }

    if (this.reportIds.length > 0) {
      // Save to sessionStorage for page reloads
      sessionStorage.setItem('headToHeadReportIds', JSON.stringify(this.reportIds))
      this.loadReports()
    }
  },
  beforeDestroy() {
    // Clear all data when leaving the component
    this.reportIds = []
    this.headingNames = []
    this.comparisonData = []
    this.error = false
    this.errorMessage = ''
  },
  methods: {
    loadReports() {
      this.loading = true
      this.error = false
      this.errorMessage = ''

      // Format as list for research.cfc
      const reportNumbers = this.reportIds.join(',')

      this.$http.get('research.cfc?method=getheadtohead', {
        params: {
          reportnumbers: reportNumbers,
        },
      })
        .then(response => {
          const { data } = response

          if (data.STATUS === 'Success') {
            this.headingNames = data.HEADINGNAMES
            // data.RESULT is column-oriented: each key (1-31) is an attribute column
            // Each column contains row data with row numbers as keys (1, 2, 3...)
            this.comparisonData = data.RESULT
          } else {
            this.error = true
            this.errorMessage = data.STATUS
          }
        })
        .catch(err => {
          this.error = true
          this.errorMessage = 'Failed to load comparison data'
          console.error('Error loading head-to-head data:', err)
        })
        .finally(() => {
          this.loading = false
        })
    },
    getCellValue(colIndex, rowIndex) {
      // result is column-oriented where keys are attribute indices
      // Each attribute column contains row data with report indices as keys
      // rowIndex = which attribute row (0-based) -> add 1 for struct key
      // colIndex = which report column (0-based) -> add 1 for struct key
      const attributeKey = String(rowIndex + 1)
      const reportKey = String(colIndex + 1)

      if (!this.comparisonData[attributeKey]) {
        return '-'
      }
      if (!this.comparisonData[attributeKey][reportKey]) {
        return '-'
      }
      const value = this.comparisonData[attributeKey][reportKey]
      return value !== undefined && value !== null && value !== '' ? value : '-'
    },
  },
}
</script>

<style scoped>
.comparison-table {
  min-width: 100%;
}

.field-column {
  min-width: 200px;
  position: sticky;
  left: 0;
  background-color: white;
  z-index: 10;
}

.report-column {
  min-width: 150px;
  padding: 0 !important;
}

.field-name {
  font-weight: 500;
  background-color: #f8f9fa;
  position: sticky;
  left: 0;
  z-index: 10;
}

.category-row {
  background-color: #e9ecef;
}

.category-row td {
  font-weight: bold;
  padding: 12px 8px;
}

.table-bordered td {
  border: 1px solid #dee2e6;
  padding: 8px;
  vertical-align: top;
}

.table-bordered th {
  border: 1px solid #dee2e6;
  padding: 8px;
  vertical-align: middle;
}

.report-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  display: block;
  width: 100%;
  height: 100%;
  padding: 8px;
  transition: background-color 0.2s ease;
}

.report-link:hover {
  background-color: #1a73e8;
  color: white;
}
</style>

<style>
/* for v-html */
.comparison-table td ul {
  padding-left: 10px !important;
  margin-left: 0 !important;
  margin-bottom: 0 !important;
  list-style-position: outside !important;
}

.comparison-table td li {
  padding-left: 0 !important;
  margin-left: 0 !important;
}

/* Dark mode support for sticky column */
.dark-layout .field-column,
.dark-layout .field-name {
  background-color: #283046;
}

.dark-layout .category-row {
  background-color: #343d55;
}
</style>
